import React from "react";
import { motion, useAnimation } from "framer-motion";
import { Check, X, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SwipeOption, VoteType } from "./utils/types";
import { formatDate, formatTime } from "./utils/dateUtils";
import { logger } from "@core/lib/logger";

interface VoteOptionMobileProps {
  option: SwipeOption;
  index: number;
  userVote: VoteType;
  userHasVoted: boolean;
  handleVote: (optionId: string, voteType: VoteType) => void;
  getStatsWithUser: (optionId: string) => {
    yes: number;
    maybe: number;
    no: number;
  };
  getExistingStats: (optionId: string) => {
    yes: number;
    maybe: number;
    no: number;
  };
  getRanking: (type: string) => Record<string, number> | number;
  anyUserHasVoted: boolean;
  totalVoters: number;
  allowMaybeVotes?: boolean;
}

const VoteOptionMobile: React.FC<VoteOptionMobileProps> = ({
  option,
  index,
  userVote,
  userHasVoted,
  handleVote,
  getStatsWithUser,
  getExistingStats,
  getRanking,
  anyUserHasVoted,
  totalVoters,
  allowMaybeVotes = true,
}) => {
  const { t } = useTranslation("polls");
  const controls = useAnimation();
  const rank = getRanking("all")[option.id];

  logger.debug("Option ranking", "vote", { optionId: option.id, rank });

  const dateLabel = option.date_group_label || formatDate(option.option_date);

  return (
    <motion.div
      key={option.id}
      className="relative"
      animate={controls}
      whileTap={{ scale: 0.98 }}
    >
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg font-semibold text-foreground">
            {/* Afficher le label du groupe si disponible, sinon la date normale */}
            {option.date_group_label || formatDate(option.option_date)}
          </span>
          {rank === 1 && totalVoters >= 2 && (
            <span
              className="text-warning text-base"
              title={t("voteOption.mostChosenSlot")}
            >
              ⭐
            </span>
          )}
          {/* Afficher les time slots uniquement pour les dates individuelles */}
          {!option.date_group_label && option.time_slots && (
            <>
              <span className="text-muted-foreground">•</span>
              <span className="text-sm text-muted-foreground">
                {formatTime(option.time_slots)}
              </span>
            </>
          )}
        </div>
      </div>

      <div
        className={`grid gap-2 relative ${allowMaybeVotes ? "grid-cols-3" : "grid-cols-2"}`}
      >
        {/* BOUTON YES (OUI) */}
        <button
          onClick={() => handleVote(option.id, "yes" as VoteType)}
          className={`relative overflow-hidden rounded-xl py-4 px-6 transition-all duration-300 border-2 hover:scale-105 ${
            userVote === "yes" && userHasVoted
              ? "bg-success/20 border-success text-success shadow-success/20"
              : "bg-muted border-border hover:bg-accent hover:border-border/80"
          }`}
          data-testid={`vote-option-yes-${option.id}`}
          aria-label={t("voteOption.yesAvailable", { date: dateLabel })}
          aria-pressed={userVote === "yes" && userHasVoted}
        >
          <div className="flex flex-col items-center gap-1 relative z-10">
            <Check
              className={`w-6 h-6 ${
                userVote === "yes" && userHasVoted
                  ? "text-success"
                  : "text-success"
              }`}
            />
            <span
              className={`text-sm font-bold bg-success/10 px-2 py-1 rounded ${
                userVote === "yes" && userHasVoted
                  ? "text-success"
                  : "text-success"
              } ${getStatsWithUser(option.id).yes === 0 ? "invisible" : ""}`}
            >
              {getStatsWithUser(option.id).yes || 0}
            </span>
          </div>

          {/* Barre de progression horizontale */}
          {(() => {
            const totalVotants =
              getExistingStats(option.id).yes +
                getExistingStats(option.id).maybe +
                getExistingStats(option.id).no || 1;
            const votesExistantsYes = getExistingStats(option.id).yes;

            return (
              <>
                {votesExistantsYes > 0 && (
                  <motion.div
                    className="absolute bottom-0 left-0 h-2 bg-gradient-to-r from-success/60 to-success/40"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${(votesExistantsYes / totalVotants) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </>
            );
          })()}
        </button>

        {/* BOUTON MAYBE (PEUT-ÊTRE) */}
        {allowMaybeVotes && (
          <button
            onClick={() => handleVote(option.id, "maybe" as VoteType)}
            className={`relative overflow-hidden rounded-xl py-4 px-6 transition-all duration-300 border-2 hover:scale-105 ${
              (userVote === "maybe" && userHasVoted) ||
              (!userHasVoted && userVote === "maybe")
                ? "bg-warning/20 border-warning text-warning shadow-warning/20"
                : "bg-muted border-border hover:bg-accent hover:border-border/80"
            }`}
            data-testid={`vote-option-maybe-${option.id}`}
            aria-label={t("voteOption.maybeAvailable", { date: dateLabel })}
            aria-pressed={userVote === "maybe" && userHasVoted}
          >
            <div className="flex flex-col items-center gap-1 relative z-10">
              <HelpCircle
                className={`w-6 h-6 ${
                  (userVote === "maybe" && userHasVoted) ||
                  (!userHasVoted && userVote === "maybe")
                    ? "text-warning"
                    : "text-warning"
                }`}
              />
              <span
                className={`text-sm font-bold bg-warning/10 px-2 py-1 rounded ${
                  (userVote === "maybe" && userHasVoted) ||
                  (!userHasVoted && userVote === "maybe")
                    ? "text-warning"
                    : "text-warning"
                } ${getStatsWithUser(option.id).maybe === 0 ? "invisible" : ""}`}
              >
                {getStatsWithUser(option.id).maybe || 0}
              </span>
            </div>

            {/* Barre de progression horizontale */}
            {(() => {
              const totalVotants =
                getExistingStats(option.id).yes +
                  getExistingStats(option.id).maybe +
                  getExistingStats(option.id).no || 1;
              const votesExistantsMaybe = getExistingStats(option.id).maybe;

              return (
                <>
                  {votesExistantsMaybe > 0 && (
                    <motion.div
                      className="absolute bottom-0 left-0 h-2 bg-gradient-to-r from-warning/60 to-warning/40"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${(votesExistantsMaybe / totalVotants) * 100}%`,
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </>
              );
            })()}
          </button>
        )}

        {/* BOUTON NO (NON) */}
        <button
          onClick={() => handleVote(option.id, "no" as VoteType)}
          className={`relative overflow-hidden rounded-xl py-4 px-6 transition-all duration-300 border-2 hover:scale-105 ${
            userVote === "no" && userHasVoted
              ? "bg-destructive/20 border-destructive text-destructive shadow-destructive/20"
              : "bg-muted border-border hover:bg-accent hover:border-border/80"
          }`}
          data-testid={`vote-option-no-${option.id}`}
          aria-label={t("voteOption.noAvailable", { date: dateLabel })}
          aria-pressed={userVote === "no" && userHasVoted}
        >
          <div className="flex flex-col items-center gap-1 relative z-10">
            <X
              className={`w-6 h-6 ${
                userVote === "no" && userHasVoted
                  ? "text-destructive"
                  : "text-destructive"
              }`}
            />
            <span
              className={`text-sm font-bold bg-destructive/10 px-2 py-1 rounded ${
                userVote === "no" && userHasVoted
                  ? "text-destructive"
                  : "text-destructive"
              } ${getStatsWithUser(option.id).no === 0 ? "invisible" : ""}`}
            >
              {getStatsWithUser(option.id).no || 0}
            </span>
          </div>

          {/* Barre de progression horizontale */}
          {(() => {
            const totalVotants =
              getExistingStats(option.id).yes +
                getExistingStats(option.id).maybe +
                getExistingStats(option.id).no || 1;
            const votesExistantsNo = getExistingStats(option.id).no;

            return (
              <>
                {votesExistantsNo > 0 && (
                  <motion.div
                    className="absolute bottom-0 left-0 h-2 bg-gradient-to-r from-destructive/60 to-destructive/40"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${(votesExistantsNo / totalVotants) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </>
            );
          })()}
        </button>
      </div>
    </motion.div>
  );
};

export default VoteOptionMobile;
